import React from "react";

import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { ArrowDownIcon } from "../assets/icons/ArrowDown";
import { SxProps, Theme } from "@mui/material/styles";

interface Props {
  content: JSX.Element;
  header: (isExpanded: boolean) => JSX.Element;
  dataTestId?: string;
  contentClassName?: string;
  contentSx?: SxProps<Theme>;
  expandOnInit?: boolean;
  withoutAccent?: boolean;
  withoutBorder?: boolean;
  arrowToRight?: boolean;
}

export const CustomAccordion: React.FC<Props> = ({
  content,
  header,
  dataTestId,
  contentClassName,
  contentSx = [],
  expandOnInit,
  arrowToRight,
  withoutAccent = false,
  withoutBorder = false,
}: Props) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(!!expandOnInit);

  return (
    <Accordion
      data-testid={dataTestId ?? "accordion"}
      sx={{
        border: 0,
        "&:before": withoutBorder
          ? {
              display: "none",
            }
          : undefined,
      }}
      expanded={isExpanded}
      onChange={() => setIsExpanded((prevState) => !prevState)}>
      <AccordionSummary
        sx={(theme) => ({
          justifyContent: "flex-start",
          paddingLeft: 0,
          paddingRight: 0,
          margin: 0,
          width: arrowToRight ? `calc(100% - ${theme.spacing(1.5)})` : "auto",
          ".MuiAccordionSummary-content": {
            flexGrow: withoutAccent ? 1 : 0,
            margin: 0,
            width: arrowToRight ? `calc(100% - ${theme.spacing(1.5)})` : "auto",
            "&:last-child": {
              marginLeft: "auto",
            },
            "& + div": {
              marginLeft: theme.spacing(2),
            },
          },
          ".Mui-expanded": {
            flexGrow: 0,
            padding: 0,
            marginY: "0px !important",
          },
        })}
        expandIcon={
          <ArrowDownIcon
            sx={(theme) => ({
              color:
                !withoutAccent && isExpanded
                  ? theme.palette.secondary.main
                  : theme.palette.text.secondary,
              width: theme.spacing(2),
              height: theme.spacing(1),
            })}
          />
        }>
        {header(isExpanded)}
      </AccordionSummary>
      <AccordionDetails
        sx={[
          (theme) => ({
            marginTop: theme.spacing(1),
            padding: 0,
            paddingX: theme.spacing(1),
            paddingBottom: theme.spacing(2.5),
          }),
          ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
        ]}
        className={contentClassName}>
        {content}
      </AccordionDetails>
    </Accordion>
  );
};
